
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

const forgotPasswordSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address" }),
});

type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

interface ForgotPasswordFormProps {
  email: string;
  setEmail: (email: string) => void;
  loading: boolean;
  onSubmit: (email: string, e: React.FormEvent) => Promise<void>;
  validationError: string;
}

const ForgotPasswordForm = ({
  email,
  setEmail,
  loading,
  onSubmit,
  validationError
}: ForgotPasswordFormProps) => {
  const form = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: email,
    },
  });
  
  const handleFormSubmit = (data: ForgotPasswordFormValues) => {
    // Update parent state
    setEmail(data.email);
    
    // Create a synthetic event to pass to the original onSubmit
    const syntheticEvent = {
      preventDefault: () => {},
    } as React.FormEvent;
    
    // Pass both the email and event to the handler
    onSubmit(data.email, syntheticEvent);
  };

  return (
    <>
      {validationError && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{validationError}</AlertDescription>
        </Alert>
      )}
      
      <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">Email address</Label>
          <Input
            id="email"
            type="email"
            required
            {...form.register("email")}
          />
          {form.formState.errors.email && (
            <p className="text-sm text-red-500">{form.formState.errors.email.message}</p>
          )}
        </div>
        <Button
          type="submit"
          className="w-full"
          disabled={loading}
        >
          {loading ? "Sending reset link..." : "Send reset link"}
        </Button>
      </form>
    </>
  );
};

export default ForgotPasswordForm;
